<template>
  <div class='notification-row'>
    <div class='row-content' @click='makeNotificationRead'>
      <div class='notification-message'>
        <span> {{ message[0] }} </span>
        <span @click='goToProject' class='project-link'> {{ message[1] }} </span>
        <span> {{ message[2] }} </span>
      </div>
      <div class='notification-date'>
        {{ notificationDate }}
      </div>
    </div>
    <div class='row-action'>
      <custom-tooltip placement="right" popper-class="light-tooltip">
        <div slot='buttonContent'>
          <span class="material-icons" :class='{ unread: !isRead }' @click='readNotification'>
              {{ readIcon }}
           </span>
        </div>
        <div slot="content">
          <slot name="tooltip">
            {{ $t('read.unread.tooltip') }}
          </slot>
        </div>
      </custom-tooltip>
      <custom-tooltip placement="right" popper-class="light-tooltip">
        <div slot='buttonContent'>
          <span class='delete_btn' @click='deleteNotification'>
             <delete-icon />
          </span>
        </div>
        <div slot="content">
          <slot name="tooltip">
            {{ $t('delete.tooltip') }}
          </slot>
        </div>
      </custom-tooltip>
    </div>
  </div>
</template>

<script>

import moment from 'moment';
import { mapGetters } from 'vuex';
import DeleteIcon from '@/assets/delete.svg?inline';
import { DELETE_NOTIFICATION, UPDATE_NOTIFICATION_STATUS } from '@/store/notification/actionTypes';
import { views } from '@/router/constants';
import { MessageType, ptProjectFilterMessageTypes } from '@/constants/notifications';
import { SET_NEW_PROJECT, SET_NEW_PROJECT_NAVIGATION } from '@/store/newProject/actionTypes';
import CustomTooltip from '../common/CustomTooltip.vue';

function checkTimeDifference(date, type) {
  const currentTime = moment();
  const timeStore = moment(date);
  return currentTime.diff(timeStore, type);
}

export default {
  name: 'NotificationRow',
  components: { DeleteIcon, CustomTooltip },
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['storedNewProject', 'isServiceProvider']),
    notificationDate() {
      const hourDifference = checkTimeDifference(this.notification.createdDate, 'hour');
      if (hourDifference > 24) {
        return moment(this.notification.createdDate).format('MMM DD HH:mm');
      }
      const minuteDifference = checkTimeDifference(this.notification.createdDate, 'minute');
      const remainingMinutes = minuteDifference - (hourDifference * 60);
      if (minuteDifference > 60) {
        return `${hourDifference}h ${remainingMinutes}m`;
      }
      const secondDifference = checkTimeDifference(this.notification.createdDate, 'second');
      const remainingSeconds = secondDifference - (remainingMinutes * 60);
      return `${remainingMinutes}m ${remainingSeconds}s`;
    },
    isRead() {
      return this.notification.status === 'read';
    },
    readIcon() {
      return this.isRead ? 'radio_button_unchecked' : 'radio_button_checked';
    },
    message() {
      if (this.notification.message.includes('"')) {
        const [notificationFirstPart, projectName, restOfMessage] = this.notification.message.split('"');
        return [notificationFirstPart, projectName, restOfMessage];
      }
      return ['', this.notification.message, ''];
    },
  },
  methods: {
    mapToObj(map) {
      const obj = {};
      const parsedMap = JSON.parse(map);
      Object.keys(parsedMap).forEach(key => {
        obj[key] = parsedMap[key];
      });
      return obj;
    },
    async makeNotificationRead() {
      if (this.notification.status === 'unread') {
        this.notification.status = 'read';
        await this.$store.dispatch(UPDATE_NOTIFICATION_STATUS, [this.notification]);
      }
    },
    async readNotification() {
      if (this.notification.status === 'read') {
        this.notification.status = 'unread';
      } else {
        this.notification.status = 'read';
      }
      await this.$store.dispatch(UPDATE_NOTIFICATION_STATUS, [this.notification]);
    },
    async deleteNotification() {
      await this.$store.dispatch(DELETE_NOTIFICATION, this.notification.id);
    },
    populateFeaturesInStoredNewProject(features) {
      const storedNewProjectWithFeatures = this.storedNewProject;
      for (let i = 0; i < storedNewProjectWithFeatures.projectResourcesData.resources.length; i += 1) {
        storedNewProjectWithFeatures.projectResourcesData.resources[i].automaticallyScoped = true;
        const featuresForResource = features[storedNewProjectWithFeatures.projectResourcesData.resources[i].value];
        for (let j = 0; j < storedNewProjectWithFeatures.projectResourcesData.resources[i].features.length; j += 1) {
          storedNewProjectWithFeatures.projectResourcesData.resources[i].features[j].selected = featuresForResource[storedNewProjectWithFeatures.projectResourcesData.resources[i].features[j].value];
        }
      }
      return storedNewProjectWithFeatures;
    },
    async prepareDataToFinishedScopingProject() {
      await this.$store.dispatch(SET_NEW_PROJECT, this.mapToObj(this.notification.metaData.projectPayload));
      await this.$store.dispatch(SET_NEW_PROJECT_NAVIGATION, this.mapToObj(this.notification.metaData.projectNavigationPayload));
      const features = this.mapToObj(this.notification.metaData.features);
      const storedNewProjectWithFeatures = this.populateFeaturesInStoredNewProject(features);
      await this.$store.dispatch(SET_NEW_PROJECT, storedNewProjectWithFeatures);
    },
    async goToProject() {
      if (this.notification.metaData.notificationType && this.notification.metaData.notificationType === MessageType.PROJECT_SCOPING_FINISHED) {
        await this.prepareDataToFinishedScopingProject();
        await this.$router.push({ name: views.NEW_PROJECT });
      } else if (this.isServiceProvider) {
        await this.$router.push({ name: views.REQUESTS });
      } else if (this.notification.metaData.projectId) {
        let to = 'projectDetails.tab.automatic.issues';
        if (this.notification.metaData.notificationType && ptProjectFilterMessageTypes.includes(this.notification.metaData.notificationType)) {
          to = 'projectDetails.tab.general';
        }
        await this.$router.push({
          name: views.PROJECT_DETAILS,
          params: { id: this.notification.metaData.projectId, to },
        });
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.notification-row {
  display: flex;
  justify-content: space-between;
  &:hover {
    cursor: pointer;
  }
  .row-content {
    .notification-message {
      width: fit-content;
      max-width: rem(300);
      color: var(--text-primary);
      font-family: $font-family-regular;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: $space-5;
      text-align: left;
      .project-link {
        color: var(--link-primary);
        &:hover {
          cursor: pointer;
        }
      }
      &:hover {
        font-family: $font-family-medium;
      }
    }
    .notification-date {
      height: $space-5;
      width: fit-content;
      max-width: rem(314);
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: $space-5;
    }
  }
  .row-action {
    margin-right: 3%;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    .delete_btn {
      margin-left: rem(-20);
      height: rem(14);
      width: rem(14);
      fill: var(--text-secondary);
      &:hover {
        cursor: pointer;
        height: $space-4;
        width: $space-4;
        fill: var(--status-error);
      }
    }
    .material-icons {
      font-weight: bolder;
      margin-right: rem(11);
      height: rem(14);
      width: $space-4;
      color: var(--text-muted);
      font-size: $font-size-base;
      &:hover {
        cursor: pointer;
        font-size: $font-size-md;
      }
      &.unread {
        color: var(--brand-primary);
      }
    }
  }
}
</style>
